/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import {
  AuthenticationService,
  CookieService,
  DataTableModule,
  PaginationModule,
  ShowHeaderMode,
  ThumbnailService,
  ViewerModule
} from '@alfresco/adf-core';
import { AfterViewInit, Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Node, NodeEntry, PathElement } from '@alfresco/js-api';
import { DocumentListDirective, NodeActionsService } from '@alfresco/aca-content';
import {
  ContentApiService,
  ContextActionsDirective,
  GenericErrorComponent,
  InfoDrawerComponent,
  PageComponent,
  PageLayoutComponent,
  PaginationDirective,
  ToolbarComponent
} from '@alfresco/aca-shared';
import { isAdmin, SetCurrentFolderAction, SetSelectedNodesAction, UploadFileVersionAction } from '@alfresco/aca-shared/store';
import { debounceTime } from 'rxjs/operators';
import {
  AlfrescoViewerModule,
  BreadcrumbComponent,
  DocumentListModule,
  FileUploadEvent,
  FilterSearch,
  ShareDataRow,
  UploadModule
} from '@alfresco/adf-content-services';
import { DocumentListPresetRef, DynamicColumnComponent } from '@alfresco/adf-extensions';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { AlgRenditionService } from '../../alg-rendition.service';
import { CommentSyncService } from '../../services/comment-sync.service';
import { CommentColumnService } from '../../services/comment-column.service';
import { TreeNode, TreeNodesComponent } from '../tree-nodes/tree-nodes.component';
import { AngularSplitModule, SplitGutterInteractionEvent } from 'angular-split';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { algAuthorizationHeader } from '../../alg-auth';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlgMaterialModule } from '../../alg-material.module';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    BreadcrumbComponent,
    GenericErrorComponent,
    UploadModule,
    DocumentListModule,
    DocumentListDirective,
    ContextActionsDirective,
    DataTableModule,
    DynamicColumnComponent,
    PaginationModule,
    MatProgressSpinnerModule,
    InfoDrawerComponent,
    PaginationDirective,
    PageLayoutComponent,
    ToolbarComponent,
    ViewerModule,
    MatIconModule,
    AngularSplitModule,
    TreeNodesComponent,
    MatButtonModule,
    MatTooltipModule,
    AlfrescoViewerModule,
    FormsModule,
    AlgMaterialModule
  ],
  templateUrl: './alg-files.component.html',
  styleUrls: ['./alg-files.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AlgFilesComponent extends PageComponent implements OnInit, OnDestroy, AfterViewInit {
  isValidPath = true;
  isAdmin = false;
  selectedNode: NodeEntry;
  queryParams = null;

  columns: DocumentListPresetRef[] = [];
  isFilterHeaderActive = false;
  treeData: TreeNode[] = [];
  public blobFile: Blob = null;
  public previewOpened = false;
  public fileName: string;
  public urlMedia: string = null;
  public urlPrint: string = null;
  public mimeType: string = null;
  public nodePreviewed: any;

  /**
   * Aucun rendu previsualisable pour ce fichier.
   *
   * Le volet affiche alors un message, au lieu de laisser tourner son indicateur de chargement —
   * ce qu'il faisait sans fin sur tout type que le serveur ne sait pas convertir en PDF.
   */
  public previewUnavailable = false;
  /** Noeud dont le rendu est en cours, sans ouvrir ni bloquer la page. */
  private pendingPreviewNodeId: string | null = null;
  /** Fichier choisi dans le tree, a ouvrir apres le chargement effectif de son dossier parent. */
  private pendingTreePreview: NodeEntry | null = null;

  /** Garde contre le clic synthetique du menu contextuel. Voir onMouseDown. */
  private suppressNextNodeClick = false;

  /** Selection presente avant le clic droit, a remettre en place a la fermeture du menu. */
  private selectionBeforeContextMenu: NodeEntry[] | null = null;
  private contextMenuObserver: MutationObserver | null = null;
  private contextMenuWasOpen = false;
  private contextMenuActionRan = false;
  private contextMenuGiveUp: ReturnType<typeof setTimeout> | null = null;
  paneSizes: number[];
  @ViewChild(TreeNodesComponent, { static: false }) treeNodes: TreeNodesComponent;
  @ViewChild('filesWorkspace', { static: false }) filesWorkspace: ElementRef<HTMLElement>;
  isCtrlPressed = false;
  public angle = 0;
  public searchTerm: string;
  public resultSearch: any;
  public isFocused = false;
  public renderingPdf: Subscription;
  renderedCanceled = false;
  public isSidebarVisible = true;
  /** Vaut true uniquement quand le tree a ete replie automatiquement pour une previsualisation. */
  private restoreTreeAfterPreview = false;
  protected readonly print = print;
  // Chaine d ancetres INTACTE. `updateCurrentNode` charcute `node.path.elements` pour le fil
  // d Ariane (retrait de User Homes, Sites, documentLibrary) ; cette copie est prise avant.
  // Liee a l arborescence, qui a besoin du chemin reel.
  nodePath: PathElement[];

  /**
   * Ecouteurs lies une seule fois.
   *
   * `removeEventListener('keydown', this.onKeyDown.bind(this))` ne retirait rien : `.bind()` rend
   * une NOUVELLE fonction a chaque appel, qui ne correspond pas a celle enregistree. Les quatre
   * ecouteurs survivaient donc au composant, et le maintenaient en vie avec eux — un jeu de plus a
   * chaque passage sur la page.
   */
  private readonly onKeyDownBound = this.onKeyDown.bind(this);
  private readonly onKeyUpBound = this.onKeyUp.bind(this);
  private readonly onWindowBlurBound = this.onWindowBlur.bind(this);
  private readonly handleOutsideClickBound = this.handleOutsideClick.bind(this);

  private readonly route = inject(ActivatedRoute);
  private readonly contentApi = inject(ContentApiService);
  private readonly algRenditionService = inject(AlgRenditionService);
  private readonly http = inject(HttpClient);
  private readonly cookieSerivce = inject(CookieService);
  private readonly authService = inject(AuthenticationService);
  private readonly commentSyncService = inject(CommentSyncService);
  readonly commentColumn = inject(CommentColumnService);
  private readonly nodeActionsService = inject(NodeActionsService);
  private readonly thumbnailService = inject(ThumbnailService);

  ngOnInit() {
    this.isSidebarVisible = this.cookieSerivce.getItem('sidebarVisible') === 'true';
    document.addEventListener('click', this.handleOutsideClickBound);
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('keyup', this.onKeyUpBound);
    window.addEventListener('blur', this.onWindowBlurBound);
    const sizesString = localStorage.getItem('paneSizes');
    if (sizesString) {
      this.paneSizes = JSON.parse(sizesString);
    } else {
      this.paneSizes = [60, 40];
    }
    super.ngOnInit();

    const { data } = this.route.snapshot;

    this.title = data.title;

    this.route.queryParamMap.subscribe((queryMap: Params) => {
      this.queryParams = queryMap.params;
    });
    this.route.params.subscribe(({ folderId }: Params) => {
      const nodeId = folderId || data.defaultNodeId;

      this.contentApi.getNode(nodeId).subscribe(
        async (node) => {
          this.isValidPath = true;

          if (node?.entry?.isFolder) {
            await this.updateCurrentNode(node.entry);
            this.openTreePreviewAfterFolderLoad(node.entry.id);
          } else {
            this.router.navigate(['/personal-files', node.entry.parentId], {
              replaceUrl: true
            });
          }
        },
        () => (this.isValidPath = false)
      );
    });

    this.subscriptions = this.subscriptions.concat([
      this.nodeActionsService.contentCopied.subscribe((nodes) => this.onContentCopied(nodes)),
      this.uploadService.fileUploadComplete.pipe(debounceTime(300)).subscribe((file) => this.onFileUploadedEvent(file)),
      this.uploadService.fileUploadDeleted.pipe(debounceTime(300)).subscribe((file) => this.onFileUploadedEvent(file))
    ]);

    this.store
      .select(isAdmin)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.isAdmin = value;
      });

    this.extensions.filesDocumentListPreset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((preset) => {
      this.columns = preset;
    });

    if (this.queryParams && Object.keys(this.queryParams).length > 0) {
      this.isFilterHeaderActive = true;
    }
  }

  ngAfterViewInit() {
    this.documentList.ready.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadInitialCommentCounts();
    });
  }

  ngOnDestroy() {
    this.store.dispatch(new SetCurrentFolderAction(null));
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('keyup', this.onKeyUpBound);
    window.removeEventListener('blur', this.onWindowBlurBound);
    document.removeEventListener('click', this.handleOutsideClickBound);
    this.stopWatchingContextMenu();
    this.renderingPdf?.unsubscribe();
    super.ngOnDestroy();
  }

  navigate(nodeId: string = null, state?: Record<string, unknown>) {
    const currentNodeId = this.route.snapshot.paramMap.get('folderId');
    const urlWithoutParams = decodeURIComponent(this.router.url).split('?')[0];
    const urlToNavigate: string[] = this.getUrlToNavigate(urlWithoutParams, currentNodeId, nodeId);
    return this.router.navigate(urlToNavigate, { state });
  }

  onUploadNewVersion(ev: CustomEvent) {
    this.store.dispatch(new UploadFileVersionAction(ev));
  }

  navigateTo(node: any) {
    if (node) {
      this.selectedNode = node;
      const { isFolder } = node.entry;
      if (isFolder) {
        let id: string;

        if (node.entry.nodeType === 'app:folderlink') {
          id = node.entry.properties['cm:destination'];
        } else {
          id = node.entry.id;
        }

        this.documentList.resetNewFolderPagination();
        this.navigate(id);
        return;
      }
      const url = window.location.href.split('/');
      url.push('(viewer:view/' + node.entry.id + ')');
      window.open(url.join('/'), '_blank');
    }
  }

  handleNodeClick(event: any, fromSearch = false) {
    if (this.treeNodes) {
      if (event?.value) {
        this.treeNodes.navigateToFromOutside(event.value.entry.id, fromSearch);
      } else if (event?.detail?.node?.entry?.id) {
        this.treeNodes.navigateToFromOutside((event as CustomEvent).detail?.node.entry.id, fromSearch);
      } else {
        this.treeNodes.navigateToFromOutside(event.entry.id, fromSearch);
      }
    } else {
      this.navigateTo(event instanceof CustomEvent ? (event as CustomEvent).detail?.node : event.value);
    }
  }

  onBreadcrumbNavigate(route: PathElement) {
    this.documentList.resetNewFolderPagination();

    // todo: review this approach once 5.2.3 is out
    if (this.nodePath && this.nodePath?.length > 2) {
      if (this.nodePath[1].name === 'Sites' && this.nodePath[2].id === route.id) {
        return this.navigate(this.nodePath[3].id);
      }
    }
    return this.navigate(route.id);
  }

  onFileUploadedEvent(event: FileUploadEvent) {
    const node: NodeEntry = event.file.data;

    // check root and child nodes
    if (node?.entry?.parentId === this.getParentNodeId()) {
      this.reload(this.selectedNode);
      return;
    }

    // check the child nodes to show dropped folder
    if (event?.file.options.parentId === this.getParentNodeId()) {
      this.displayFolderParent(0, event.file.options.path);
      return;
    }

    if (event?.file.options.parentId) {
      if (this.nodePath) {
        const correspondingNodePath = this.nodePath.find((pathItem) => pathItem.id === event.file.options.parentId);

        // check if the current folder has the 'trigger-upload-folder' as one of its parents
        if (correspondingNodePath) {
          const correspondingIndex = this.nodePath.length - this.nodePath.indexOf(correspondingNodePath);
          this.displayFolderParent(correspondingIndex, event.file.options.path);
        }
      }
    }
  }

  displayFolderParent(index: number, filePath = '') {
    const parentName = filePath.split('/')[index];
    const currentFoldersDisplayed = (this.documentList.data.getRows() as ShareDataRow[]) || [];

    const alreadyDisplayedParentFolder = currentFoldersDisplayed.find((row) => row.node.entry.isFolder && row.node.entry.name === parentName);

    if (alreadyDisplayedParentFolder) {
      return;
    }
    this.reload(this.selectedNode);
  }

  onContentCopied(nodes: NodeEntry[]) {
    const newNode = nodes.find((node) => node?.entry?.parentId === this.getParentNodeId());
    if (newNode) {
      this.reload(this.selectedNode);
    }
  }

  isSiteContainer(node: Node): boolean {
    if (node?.aspectNames?.length > 0) {
      return node.aspectNames.indexOf('st:siteContainer') >= 0;
    }
    return false;
  }

  isRootNode(nodeId: string): boolean {
    if (this.node?.path?.elements?.length > 0) {
      return this.node.path.elements[0].id === nodeId;
    }
    return false;
  }

  onFilterSelected(activeFilters: FilterSearch[]) {
    if (activeFilters.length) {
      this.showHeader = ShowHeaderMode.Always;
      this.isFilterHeaderActive = true;
      this.navigateToFilter(activeFilters);
    } else {
      this.router.navigate(['.'], { relativeTo: this.route });
      this.isFilterHeaderActive = false;
      this.showHeader = ShowHeaderMode.Data;
    }
  }

  navigateToFilter(activeFilters: FilterSearch[]) {
    const objectFromMap = {};
    activeFilters.forEach((filter: FilterSearch) => {
      let paramValue;
      if (filter?.value?.from && filter?.value?.to) {
        paramValue = `${filter.value.from}||${filter.value.to}`;
      } else {
        paramValue = filter.value;
      }
      objectFromMap[filter.key] = paramValue;
    });

    this.router.navigate([], { relativeTo: this.route, queryParams: objectFromMap });
  }

  onError() {
    this.isValidPath = false;
  }

  onDragEnd(event: SplitGutterInteractionEvent) {
    // Convertir les tailles en chaîne JSON pour le stockage
    const sizesString = JSON.stringify(event.sizes);
    localStorage.setItem('paneSizes', sizesString);
  }

  closePreview() {
    this.renderedCanceled = true;
    this.pendingPreviewNodeId = null;
    this.renderingPdf?.unsubscribe();
    this.previewOpened = false;
    this.blobFile = null;
    this.fileName = null;
    this.urlMedia = null;
    this.urlPrint = null;
    this.documentList.resetSelection();

    // La selection remonte aussi au magasin : sans quoi la barre d'outils continuerait de proposer
    // les actions d'un fichier qui n'est plus selectionne.
    this.store.dispatch(new SetSelectedNodesAction([]));

    // Et l'arborescence deselectionne le fichier — le dossier courant, lui, reste en evidence.
    this.treeNodes?.clearFileSelection();

    if (this.restoreTreeAfterPreview) {
      this.isSidebarVisible = true;
      this.restoreTreeAfterPreview = false;
    }
  }

  /**
   * Un clic droit ne doit pas ouvrir la previsualisation.
   *
   * `ContextActionsDirective` d'ACA, pour que son menu porte sur la ligne visee, dispatche un clic
   * SYNTHETIQUE sur la cellule :
   *
   *     if (!this.isSelected(target)) { target.dispatchEvent(new MouseEvent('click')); }
   *
   * Ce clic remonte comme un `name-click` ordinaire jusqu'a `nodeClick`, qui ouvrait donc le volet
   * de previsualisation sur un simple clic droit.
   *
   * La garde est posee sur `mousedown`, qui precede `contextmenu` de facon deterministe — se
   * brancher sur `contextmenu` reviendrait a dependre de l'ordre d'enregistrement de deux
   * ecouteurs sur le meme element. Elle est levee au tour de boucle suivant : la sequence
   * contextmenu -> clic synthetique -> name-click est synchrone, alors qu'un vrai clic ulterieur
   * arrive plus tard et ne doit pas etre avale.
   *
   * La selection, elle, est traitee juste apres : voir `rememberSelectionForContextMenu`.
   */
  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    if (event.button === 2) {
      this.suppressNextNodeClick = true;
      setTimeout(() => (this.suppressNextNodeClick = false));
      this.rememberSelectionForContextMenu();
    }
  }

  /**
   * Un clic droit ne doit pas changer la selection.
   *
   * POURQUOI CE DETOUR. `ContextActionsDirective` d'ACA selectionne DELIBEREMENT la ligne visee,
   * parce que son menu ne lit que `app.selection` : il n'a pas de cible propre. Lui en donner une
   * demanderait de modifier du code communautaire, ce que ce fork s'interdit. La selection est donc
   * remise en place APRES coup, ce qui laisse au menu la cible dont il a besoin le temps de vivre.
   *
   * Le seul signal fidele de fermeture est la disparition du menu du DOM : `ContextMenuOverlayRef`
   * n'expose qu'un `close()` et l'overlay est cree dans un effet hors de notre portee. Se brancher
   * sur les evenements qui ferment le menu (echappement, clic au dehors) manquerait le cas ou il se
   * ferme parce qu'une action a ete choisie.
   *
   * ET ON NE RESTAURE PAS SI UNE ACTION A ETE CHOISIE. Les effets d'ACA lisent `app.selection` au
   * moment ou ils s'executent ; une boite de dialogue ouverte par le menu — copier, deplacer — la
   * relit ensuite. Restaurer sous ses pieds la ferait porter sur le mauvais noeud. Un clic dans le
   * menu vaut donc consentement a la selection qu'il a posee.
   */
  private rememberSelectionForContextMenu(): void {
    this.stopWatchingContextMenu();

    this.selectionBeforeContextMenu = [...(this.documentList?.selection ?? [])];
    this.contextMenuWasOpen = false;
    this.contextMenuActionRan = false;

    document.addEventListener('click', this.noteContextMenuActionBound, true);

    this.contextMenuObserver = new MutationObserver(() => {
      const open = !!document.querySelector('aca-context-menu');

      if (open) {
        this.contextMenuWasOpen = true;
        return;
      }

      if (this.contextMenuWasOpen) {
        this.restoreSelectionAfterContextMenu();
      }
    });
    this.contextMenuObserver.observe(document.body, { childList: true, subtree: true });

    // Filet. Le menu s'ouvre apres un `debounceTime(300)`, et il ne s'ouvre PAS du tout sur une
    // liste vide — `execute()` rend la main sur `isEmptyTable`, apres avoir emis son clic. Sans
    // cette echeance, l'observateur resterait branche a attendre une ouverture qui ne vient pas.
    this.contextMenuGiveUp = setTimeout(() => {
      if (!this.contextMenuWasOpen) {
        this.restoreSelectionAfterContextMenu();
      }
    }, 1500);
  }

  /** Un clic dans le menu signifie qu'une action a ete choisie : la selection lui appartient. */
  private noteContextMenuAction(event: Event): void {
    // Le contenu de `mat-menu` est teleporte dans le CDK overlay : le bouton clique n'est pas un
    // descendant du composant hote <aca-context-menu>. Material recopie en revanche sa classe
    // `aca-context-menu` sur le panneau de l'overlay. Ne tester que le nom de balise faisait donc
    // croire qu'aucune action n'avait ete lancee ; la fermeture restaurait l'ancienne selection
    // (souvent vide), et le reducer ACA refermait aussitot le tiroir d'informations.
    if ((event.target as Element)?.closest?.('aca-context-menu, .aca-context-menu')) {
      this.contextMenuActionRan = true;
    }
  }

  private readonly noteContextMenuActionBound = this.noteContextMenuAction.bind(this);

  private restoreSelectionAfterContextMenu(): void {
    const previous = this.selectionBeforeContextMenu;
    const actionRan = this.contextMenuActionRan;
    this.stopWatchingContextMenu();

    if (!previous || actionRan) {
      return;
    }

    this.documentList.resetSelection();
    if (previous.length) {
      this.documentList.preselectNodes = previous;
      this.documentList.onPreselectNodes();
    }

    // Meme raison que dans `selectClickedRow` : `onPreselectNodes()` n'informe pas le magasin.
    this.store.dispatch(new SetSelectedNodesAction(previous));
  }

  private stopWatchingContextMenu(): void {
    this.contextMenuObserver?.disconnect();
    this.contextMenuObserver = null;
    this.selectionBeforeContextMenu = null;
    this.contextMenuWasOpen = false;
    this.contextMenuActionRan = false;

    if (this.contextMenuGiveUp) {
      clearTimeout(this.contextMenuGiveUp);
      this.contextMenuGiveUp = null;
    }

    document.removeEventListener('click', this.noteContextMenuActionBound, true);
  }

  nodeClick(customEvent: any, fromSearch = false) {
    if (this.suppressNextNodeClick) {
      return;
    }

    let node = null;
    this.angle = 0;
    if (customEvent.detail) {
      node = customEvent.detail.node.entry;
    } else if (customEvent.value) {
      node = customEvent.value.entry;
    } else {
      node = customEvent.entry;
    }
    if (!node?.isFolder) {
      if (fromSearch) {
        this.treeNodes.navigateToFromOutside(node.parentId);
      }
      if (this.isCtrlPressed) {
        this.navigateTo({ entry: node });
      } else {
        this.collapseTreeForPreview();
        this.selectClickedRow({ entry: node }, fromSearch);
        this.previewNodeOrLink(node);
      }
    } else {
      this.blobFile = null;
      this.urlMedia = null;
      this.urlPrint = null;
      this.previewOpened = false;
      if (this.isCtrlPressed) {
        const url = window.location.href.split('/');
        url[url.length - 1] = node.id;
        window.open(url.join('/'), '_blank');
      } else {
        this.handleNodeClick(customEvent, fromSearch);
      }
    }

    this.searchTerm = '';
    this.resultSearch = [];
  }

  /**
   * Icone d'un resultat de recherche, d'apres son type MIME.
   *
   * Meme resolution que la liste de documents et l'arborescence : les trois vues montrent donc la
   * meme icone pour un meme fichier. Le panneau affichait auparavant `insert_drive_file` pour tous
   * les fichiers, sans distinction.
   */
  searchResultIcon(entry: any): string {
    return entry?.content?.mimeType ? this.thumbnailService.getMimeTypeIcon(entry.content.mimeType) : this.thumbnailService.getDefaultMimeTypeIcon();
  }

  /**
   * Dossier qui contient un resultat de recherche.
   *
   * La recherche porte sur tout le sous-arbre du dossier courant : sans cette indication, deux
   * fichiers homonymes ranges dans deux dossiers differents etaient indistinguables. `path.name`
   * est fourni par la requete, qui demande `include: ['path', ...]`.
   */
  searchResultLocation(entry: any): string {
    const segments = (entry?.path?.name ?? '').split('/').filter(Boolean);

    return segments.length ? segments[segments.length - 1] : '';
  }

  /** Vide le champ de recherche dans le dossier courant, et ses resultats. */
  clearFolderSearch() {
    this.searchTerm = '';
    this.resultSearch = [];
    this.isFocused = false;
  }

  /**
   * Un fichier a ete choisi dans l'arborescence.
   *
   * L'arborescence ne connait du noeud que ce qu'elle affiche ; on recupere donc le noeud complet
   * avant de le previsualiser, en reutilisant le meme chemin qu'un clic dans la liste — resolution
   * des raccourcis et mise a jour de la selection comprises.
   */
  onTreeFileSelected(treeNode: TreeNode) {
    if (!treeNode?.nodeId) {
      return;
    }

    // La deselection part de l'instance RACINE de l'arborescence, la seule qui voie tout l'arbre :
    // le fichier precedemment ouvert peut se trouver dans une autre branche.
    this.treeNodes?.clearFileSelection();
    treeNode.current = true;

    // L'arborescence ne connait du noeud que ce qu'elle affiche ; on recupere le noeud complet,
    // dont le type et les proprietes necessaires a la resolution des raccourcis.
    this.contentApi.getNode(treeNode.nodeId).subscribe({
      next: (node) => {
        const parentId = node.entry.parentId;

        if (parentId && parentId !== this.node?.id) {
          // Le fichier vit dans un autre dossier : on y va, pour que la liste montre bien le
          // dossier du fichier ouvert.
          //
          // `preselectNodes` est pose AVANT la navigation : ADF applique la preselection quand les
          // lignes du nouveau dossier arrivent (`onDataReady`), la ligne sera donc mise en
          // evidence sans que nous ayons a attendre le chargement.
          this.documentList.preselectNodes = [node];
          this.pendingTreePreview = node;
          this.collapseTreeForPreview();
          this.navigate(parentId, { algPreviewNodeId: node.entry.id }).then((navigated) => {
            if (!navigated) {
              this.pendingTreePreview = null;
              this.previewNodeOrLink(node.entry);
            }
          });
        } else {
          this.selectClickedRow(node, false);
          this.store.dispatch(new SetSelectedNodesAction([node]));
          this.collapseTreeForPreview();
          this.previewNodeOrLink(node.entry);
        }
      },
      error: () => (this.previewOpened = false)
    });
  }

  /**
   * Ouvre le fichier demande par le tree seulement une fois son dossier parent actif.
   *
   * L'etat du routeur couvre aussi le cas ou Angular detruit puis recree la page en changeant de
   * racine (bibliotheque/site/fichiers personnels). La propriete locale couvre le changement de
   * simple parametre, pour lequel Angular reutilise normalement la meme instance.
   */
  private openTreePreviewAfterFolderLoad(folderId: string): void {
    const statePreviewId = window.history.state?.algPreviewNodeId as string | undefined;
    const localPreview = this.pendingTreePreview?.entry?.parentId === folderId ? this.pendingTreePreview : null;

    if (statePreviewId) {
      // Consommation unique : revenir plus tard dans l'historique ne doit pas rouvrir le fichier.
      const remainingState = { ...window.history.state };
      delete remainingState.algPreviewNodeId;
      window.history.replaceState(remainingState, document.title);
    }

    if (localPreview) {
      this.pendingTreePreview = null;
      this.selectTreePreview(localPreview);
      return;
    }

    if (!statePreviewId) {
      return;
    }

    this.contentApi.getNode(statePreviewId).subscribe({
      next: (preview) => {
        if (preview.entry.parentId === folderId) {
          this.documentList.preselectNodes = [preview];
          this.selectTreePreview(preview);
        }
      },
      error: () => (this.previewOpened = false)
    });
  }

  private selectTreePreview(preview: NodeEntry): void {
    this.store.dispatch(new SetSelectedNodesAction([preview]));
    this.collapseTreeForPreview();
    this.previewNodeOrLink(preview.entry);
  }

  /**
   * Marque la ligne cliquee comme selectionnee, sans recharger la liste.
   *
   * Ce que faisait la version precedente, a CHAQUE clic sur un fichier :
   *
   *   1. un `contentApi.getNode(node.id)` — alors que l'evenement de clic porte deja le noeud
   *      complet ; ADF ne se sert de l'objet que pour retrouver la ligne par son id ;
   *   2. un `documentList.reloadWithoutResettingSelection()`. Cette methode ne re-rend
   *      localement que si `documentList.node` est pose. Ici il ne l'est jamais — `nodeResult`
   *      n'est alimente qu'en mode resultats — donc elle tombait dans la branche `loadFolder()` :
   *      un vrai aller-retour reseau pour recharger tout le dossier. D'ou l'impression d'un
   *      rechargement de page a chaque selection de fichier.
   *
   * `onPreselectNodes()` est precisement la methode qu'ADF appelle en interne pour appliquer une
   * preselection : elle retrouve les lignes concernees, les selectionne et emet l'evenement de
   * selection. Aucun appel, aucun re-rendu de la liste.
   */
  private selectClickedRow(nodeEntry: NodeEntry, fromSearch: boolean) {
    if (fromSearch) {
      return;
    }

    // `resetSelection()` d'abord : `onPreselectNodes()` ne fait qu'AJOUTER une selection. Le
    // rechargement precedent deselectionnait l'ancienne ligne par effet de bord, en reconstruisant
    // les lignes ; sans cet appel explicite, passer d'un document a l'autre laissait les deux
    // selectionnes.
    this.documentList.resetSelection();
    this.documentList.preselectNodes = [nodeEntry];
    this.documentList.onPreselectNodes();

    // Et la selection est poussee au magasin a la main.
    //
    // `onPreselectNodes()` marque bien la ligne, mais il emet son evenement `node-select` avec
    // `row: undefined`, ce qui donne `detail.node = null`. Or la directive `acaDocumentList` d'ACA
    // se garde sur `!!event.detail.node` avant d'appeler sa mise a jour : l'evenement est donc
    // ignore et le magasin n'apprend jamais la selection. La ligne s'affichait selectionnee, mais
    // la barre d'outils et « Plus d'actions » continuaient de travailler sur du vide.
    //
    // Le rechargement precedent y arrivait par un autre chemin : il declenchait `onDataReady`,
    // donc `documentList.ready`, auquel ACA reagit en publiant la selection. En le supprimant, ce
    // chemin a disparu — d'ou cet appel explicite.
    //
    // On publie le noeud clique, et non `documentList.selection` : ce dernier n'est renseigne que
    // si `onPreselectNodes()` a retrouve la ligne dans ses donnees, ce qui depend de son etat
    // interne. Le noeud clique, lui, est connu a coup sur.
    this.store.dispatch(new SetSelectedNodesAction([nodeEntry]));
  }

  /**
   * Previsualise un noeud, en resolvant d'abord les raccourcis.
   *
   * Un `app:filelink` ne porte pas de `content` : il pointe vers un autre noeud par
   * `cm:destination`. Sans cette resolution, la lecture de `node.content.mimeType` levait une
   * TypeError et le volet restait bloque sur son indicateur de chargement. ACA fait deja le meme
   * detour pour `app:folderlink` a la navigation ; on l'applique ici a la previsualisation.
   */
  private previewNodeOrLink(node: any) {
    const destinationId = node?.nodeType === 'app:filelink' ? node.properties?.['cm:destination'] : null;

    if (destinationId) {
      this.contentApi.getNode(destinationId).subscribe({
        next: (target) => this.previewNode(target.entry),
        error: () => {
          // Cible supprimee ou illisible : on ferme plutot que de laisser tourner le chargement.
          this.previewOpened = false;
        }
      });
      return;
    }

    if (!node?.content) {
      // Ni contenu ni destination : il n'y a rien a afficher.
      this.previewOpened = false;
      return;
    }

    this.previewNode(node);
  }

  private previewNode(node: any) {
    if (this.nodePreviewed?.id === node.id && (this.previewOpened || this.pendingPreviewNodeId === node.id)) {
      return;
    }

    // Un rendu encore en vol appartient au fichier precedent : on l'abandonne, et on leve le drapeau
    // d'annulation pose par la fermeture — sans quoi le prochain rendu serait jete a son arrivee.
    this.renderingPdf?.unsubscribe();
    this.renderedCanceled = false;
    this.previewUnavailable = false;
    this.pendingPreviewNodeId = node.id;

    const mimeType = node.content.mimeType;
    // URL construite par ADF, et non a la main : elle porte le ticket d'authentification.
    // Un `<video src>` ne peut pas ajouter d'en-tete Authorization — l'URL assemblee ici a la main
    // partait donc en 401 et la lecture video ne demarrait jamais. Meme chose pour l'impression.
    const contentUrl = this.contentApi.getContentUrl(node.id);

    this.blobFile = null;
    this.fileName = null;
    this.urlMedia = null;
    this.urlPrint = null;
    this.mimeType = mimeType;
    this.fileName = node.name;
    // Le volet s'ouvre tout de suite avec un indicateur LOCAL. Contrairement au loader ADF natif,
    // celui-ci ne sort jamais du panneau et ne masque donc ni la liste ni l'arborescence.
    this.previewOpened = true;
    this.collapseTreeForPreview();
    this.nodePreviewed = node;

    if (mimeType === 'application/pdf' || mimeType.includes('image')) {
      this.fetchPdfDirectly(node.id).subscribe({
        next: (res: any) => {
          if (res) {
            if (this.pendingPreviewNodeId !== node.id) {
              return;
            }
            this.fileName = node.name;
            this.blobFile = res;
            this.openPreviewPane();
            this.urlPrint = contentUrl;
          }
        },
        error: () => this.showUnavailablePreview(node)
      });
    } else if (mimeType.includes('video')) {
      this.fileName = node.name;
      this.urlMedia = contentUrl;
      this.openPreviewPane();
    } else {
      // Tout le reste passe par un rendu PDF. Chaque branche a desormais un chemin d'erreur : sans
      // lui, un type que le serveur ne sait pas convertir laissait le volet tourner sans fin sur
      // son indicateur de chargement — c'est ce qu'on observait sur les fichiers .sample.
      this.algRenditionService.getRendition(node.id).subscribe({
        next: (rendition: any) => {
          if (rendition?.entry?.status === 'CREATED') {
            this.fetchAndDisplayPdf(node.id, node.name);
            this.urlPrint = contentUrl;
            return;
          }

          this.algRenditionService.requestPdfRendition(node.id).subscribe({
            next: (status) => {
              if (status === 'CREATED') {
                this.fetchAndDisplayPdf(node.id, node.name);
                this.urlPrint = contentUrl;
              } else {
                this.showUnavailablePreview(node);
              }
            },
            error: () => this.showUnavailablePreview(node)
          });
        },
        error: () => this.showUnavailablePreview(node)
      });
    }
  }

  /**
   * Recupere le rendu PDF et l'affiche, sauf si la previsualisation a ete fermee entre-temps.
   *
   * `renderedCanceled` sert a jeter un rendu qui arrive apres une fermeture. Il est desormais remis
   * a `false` a l'OUVERTURE (voir `previewNode`), et non plus seulement a la reception d'une
   * reponse : sans cela, il restait a `true` depuis la derniere fermeture, et le premier rendu
   * demande ensuite etait systematiquement jete. Le volet s'ouvrait sur son indicateur de
   * chargement et n'affichait jamais rien — visible sur tous les formats passant par un rendu, donc
   * tous les documents bureautiques.
   */
  fetchAndDisplayPdf(nodeId, fileName) {
    this.renderingPdf = this.algRenditionService.getRenderedPdf(nodeId).subscribe(
      (res: any) => {
        if (res && !this.renderedCanceled && this.pendingPreviewNodeId === nodeId) {
          this.fileName = fileName;
          this.blobFile = res;
          this.openPreviewPane();
        }
      },
      () => this.showUnavailablePreview(this.nodePreviewed)
    );
  }

  /** Ouvre le volet sans toucher au choix persistant de l'utilisateur pour l'arborescence. */
  private openPreviewPane(): void {
    this.pendingPreviewNodeId = null;
    this.previewOpened = true;

    // Trois panneaux ne restent confortables que sur un grand ecran. Sur une largeur compacte,
    // l'arbre se replie pour donner la priorite au document, mais son bouton reste disponible.
    this.collapseTreeForPreview();
  }

  private collapseTreeForPreview(): void {
    const availableWidth = this.filesWorkspace?.nativeElement.clientWidth || document.documentElement.clientWidth;

    if (availableWidth <= 700) {
      this.restoreTreeAfterPreview ||= this.isSidebarVisible;
      this.paneSizes = [0, 100];
      this.isSidebarVisible = false;
    } else if (availableWidth <= 1600) {
      this.restoreTreeAfterPreview ||= this.isSidebarVisible;
      this.paneSizes = [40, 60];
      this.isSidebarVisible = false;
    }
  }

  private showUnavailablePreview(node: any): void {
    if (!node?.id || this.pendingPreviewNodeId !== node.id) {
      return;
    }

    this.fileName = node.name;
    this.previewUnavailable = true;
    this.openPreviewPane();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.isCtrlPressed = true;
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.isCtrlPressed = false;
    }
  }

  onWindowBlur() {
    this.isCtrlPressed = false;
  }

  fetchPdfDirectly(nodeId) {
    const endpointUrl = window.location.origin + `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content`;

    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);
    headers = headers.append('Content-Type', 'application/pdf;charset=UTF-8');

    const params = {
      headers: headers,
      responseType: 'blob' as 'json'
    };

    return this.http.get(endpointUrl, params);
  }

  rotate(direction: 'left' | 'right') {
    this.angle += direction === 'left' ? -90 : 90;

    let elements: NodeListOf<Element> | null = document.querySelectorAll('.page');
    if (elements.length === 0) {
      elements = document.querySelectorAll('.adf-image-container');
    }

    this.applyRotation(elements);
  }

  openSidebarDetail(event: Event, row: ShareDataRow) {
    this.commentColumn.open(event, row, this.documentList);
  }

  /**
   * Appelee par le gabarit, donc a chaque cycle de detection de changements et pour chaque ligne.
   *
   * Elle s'abonnait au sujet sans jamais se desabonner : un abonnement par ligne et par cycle, qui
   * s'accumulaient sans fin. Elle creait de surcroit le sujet manquant, c'est-a-dire une mutation
   * d'etat pendant la detection de changements. On lit maintenant la valeur courante, ce qui donne
   * exactement le meme resultat — un BehaviorSubject emet de toute facon sa valeur immediatement.
   */
  getCommentCountForNode(row: any): number {
    return this.commentSyncService.peekCommentCount(row.getId());
  }

  searchInCurrentNode(node: Node) {
    if (this.searchTerm.length > 1) {
      const term = this.searchTerm;
      const matches = [`cm:name:'${term}*'`, `cm:title:'${term}*'`, `cm:description:'${term}*'`, `TEXT:'${term}*'`, `TAG:'${term}*'`].join(' OR ');
      const query = {
        query: {
          query: `((${matches})) AND ANCESTOR:"workspace://SpacesStore/${node.id}"`,
          language: 'afts'
        },
        include: ['path', 'allowableOperations', 'properties'],
        paging: {
          skipCount: 0,
          maxItems: 10
        },
        filterQueries: [],
        sort: [
          {
            type: 'SCORE',
            field: 'score',
            ascending: false
          }
        ],
        highlight: null,
        facetFormat: 'V2'
      };
      const endpointUrl = window.location.origin + `/alfresco/api/-default-/public/search/versions/1/search`;
      let headers = new HttpHeaders();
      const auth = algAuthorizationHeader(this.authService);
      headers = headers.append('Authorization', auth);
      const params = { headers: headers };
      this.http.post(endpointUrl, query, params).subscribe((res: any) => {
        this.resultSearch = res.list.entries;
        if (this.searchTerm.length > 1) {
          this.isFocused = this.resultSearch.length > 0;
        }
      });
    } else {
      this.resultSearch = [];
      this.isFocused = false;
    }
  }

  onInputFocus() {
    this.isFocused = true;
  }

  containerClicked(event: MouseEvent) {
    event.stopPropagation();
  }

  handleOutsideClick() {
    this.isFocused = false;
  }

  /**
   * Charge le nombre de commentaires de chaque ligne de la liste.
   *
   * La cle est `row.getId()`. Elle etait `row.id`, qui n'existe pas sur un `ShareDataRow` — celui-ci
   * n'expose que `getId()`. Tous les compteurs initiaux etaient donc ranges sous la cle `undefined`,
   * alors que le gabarit les relit par `getId()` : la colonne restait vide jusqu'a ce qu'un
   * commentaire soit ajoute dans la session.
   *
   * On passe aussi par `updateCommentCount`, qui pousse dans le sujet existant au lieu de le
   * remplacer : un abonne garde ainsi sa source.
   */
  private loadInitialCommentCounts() {
    for (const row of this.documentList.data.getRows()) {
      const nodeId = row.getId();
      const properties = row.obj?.entry?.properties ?? {};
      const commentCount = Object.prototype.hasOwnProperty.call(properties, 'fm:commentCount') ? properties['fm:commentCount'] : 0;

      this.commentSyncService.updateCommentCount(nodeId, commentCount);
    }
  }

  private getUrlToNavigate(currentURL: string, currentNodeId: string, nextNodeId: string): string[] {
    return currentNodeId ? this.getNextNodeUrlToNavigate(currentURL, currentNodeId, nextNodeId) : this.appendNextNodeIdToUrl(currentURL, nextNodeId);
  }

  private getNextNodeUrlToNavigate(currentURL: string, currentNodeId: string, nextNodeId: string): string[] {
    const urlToNavigate: string[] =
      nextNodeId && !this.isRootNode(nextNodeId)
        ? this.replaceCurrentNodeIdWithNextNodeId(currentURL, currentNodeId, nextNodeId)
        : this.removeNodeIdFromUrl(currentURL, currentNodeId);
    urlToNavigate.shift();
    return urlToNavigate;
  }

  private replaceCurrentNodeIdWithNextNodeId(currentURL: string, currentNodeId: string, nextNodeId: string): string[] {
    const nextNodeUrlToNavigate = currentURL.split('/');
    const index = nextNodeUrlToNavigate.indexOf(currentNodeId);
    if (index > 0) {
      nextNodeUrlToNavigate[index] = nextNodeId;
    }
    return nextNodeUrlToNavigate;
  }

  private removeNodeIdFromUrl(currentURL: string, currentNodeId: string): string[] {
    const rootUrl: string[] = currentURL.replace(currentNodeId, '').split('/');
    rootUrl.pop();
    return rootUrl;
  }

  private appendNextNodeIdToUrl(currentURL: string, nodeId: string): string[] {
    const navigateToNodeUrl = currentURL.split('/');
    if (nodeId && !this.isRootNode(nodeId)) {
      navigateToNodeUrl.push(nodeId);
    }
    navigateToNodeUrl.shift();
    return navigateToNodeUrl;
  }

  // todo: review this approach once 5.2.3 is out
  private async updateCurrentNode(node: Node) {
    this.nodePath = null;

    if (node?.path?.elements) {
      const elements = node.path.elements;

      this.nodePath = elements.map((pathElement) => {
        return { ...pathElement };
      });

      if (elements.length > 1) {
        if (elements[1].name === 'User Homes') {
          if (!this.isAdmin) {
            elements.splice(0, 2);
          }
        } else if (elements[1].name === 'Sites') {
          await this.normalizeSitePath(node);
        }
      }
    }

    this.node = node;
    this.store.dispatch(new SetCurrentFolderAction(node));
  }

  private async normalizeSitePath(node: Node) {
    const elements = node.path.elements;

    // remove 'Sites'
    elements.splice(1, 1);

    if (this.isSiteContainer(node)) {
      // rename 'documentLibrary' entry to the target site display name
      // clicking on the breadcrumb entry loads the site content
      const parentNode = await this.contentApi.getNodeInfo(node.parentId).toPromise();
      node.name = parentNode.properties['cm:title'] || parentNode.name;

      // remove the site entry
      elements.splice(1, 1);
    } else {
      // remove 'documentLibrary' in the middle of the path
      const docLib = elements.findIndex((el) => el.name === 'documentLibrary');
      if (docLib > -1) {
        const siteFragment = elements[docLib - 1];
        const siteNode = await this.contentApi.getNodeInfo(siteFragment.id).toPromise();

        // apply Site Name to the parent fragment
        siteFragment.name = siteNode.properties['cm:title'] || siteNode.name;
        elements.splice(docLib, 1);
      }
    }
  }

  private applyRotation(elements: NodeListOf<Element> | null) {
    if (elements) {
      elements.forEach((element) => {
        (element as HTMLElement).style.transform = `rotate(${this.angle}deg)`;
      });
    }
  }

  toggleSidebar() {
    this.isSidebarVisible = !this.isSidebarVisible;
    this.cookieSerivce.setItem('sidebarVisible', this.isSidebarVisible.toString());
  }
}
